import React, { useState, useEffect } from 'react';
import ArrowDown from '../assets/svgs/ArrowDown';

export const Select = ({
    options = [],
    defaultValue = '',
    color = 'honeysuckle',
    onChange = () => {},
    variant = 'default', // 'default' or 'enhanced'
    mobileTitle = 'Select an option' // Title for mobile fullscreen view
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [value, setValue] = useState(defaultValue);

    const openDropdown = () => setIsOpen(true);
    const closeDropdown = () => setIsOpen(false);

    const handleSelect = (item) => {
        setValue(item);
        onChange(item);
        closeDropdown();
    };

    // Close dropdown when clicking outside (for enhanced variant)
    useEffect(() => {
        if (variant === 'enhanced' && isOpen) {
            const handleClickOutside = (event) => {
                if (!event.target.closest('.enhanced-select-container')) {
                    closeDropdown();
                }
            };
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen, variant]);

    // Prevent body scroll when mobile dropdown is open
    useEffect(() => {
        if (variant === 'enhanced' && isOpen) {
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = 'unset';
            };
        }
    }, [isOpen, variant]);

    if (variant === 'enhanced') {
        return (
            <div className="enhanced-select-container relative w-full">
                <div
                    onClick={openDropdown}
                    className={`border-b-3 pb-1 text-${color} border-${color} text-xs sm:text-sm xl:text-base flex items-center justify-between gap-3 lg:gap-1 xl:gap-2 cursor-pointer`}
                >
                    <div>{value}</div>
                    <ArrowDown className={`text-${color} opacity-0.5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                </div>

                {isOpen && (
                    <>
                        {/* Mobile: Full screen overlay */}
                        <div className="lg:hidden fixed inset-0 z-50 bg-gableGreen">
                            <div className="h-full flex flex-col">
                                {/* Header */}
                                <div className="flex justify-between items-center p-6 border-b border-honeysuckle">
                                    <h3 className={`text-${color} text-lg font-medium`}>{mobileTitle}</h3>
                                    <button
                                        onClick={closeDropdown}
                                        className={`text-${color} text-2xl hover:opacity-75`}
                                    >
                                        ×
                                    </button>
                                </div>

                                {/* Scrollable options */}
                                <div className="flex-1 overflow-y-auto">
                                    {options.map((item, i) => (
                                        <div
                                            key={i}
                                            onClick={() => handleSelect(item)}
                                            className={`text-${color} cursor-pointer text-base block px-6 py-4 border-b border-opacity-20 border-${color} hover:bg-lightGrey active:bg-lightGrey`}
                                        >
                                            {item}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Desktop: Fixed height dropdown */}
                        <div className="hidden lg:block absolute top-full left-0 w-full z-50 mt-1">
                            <div
                                className={`border rounded-lg border-${color} bg-gableGreen shadow-lg max-h-60 overflow-y-auto`}
                                role="menu"
                            >
                                {options.map((item, i) => (
                                    <div
                                        key={i}
                                        onClick={() => handleSelect(item)}
                                        className={`text-${color} cursor-pointer text-xs sm:text-sm xl:text-base block px-4 py-2 hover:bg-lightGrey first:rounded-t-lg last:rounded-b-lg`}
                                    >
                                        {item}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>
        );
    }

    // Default variant (existing behavior)
    return (
        <div className="relative w-full" onMouseLeave={closeDropdown}>
            <div onClick={openDropdown} className={`border-b-3 pb-1 text-${color} border-${color} text-xs sm:text-sm xl:text-base flex items-center justify-between gap-3 lg:gap-1 xl:gap-2 cursor-pointer`}>
                <div>{value}</div>
                <ArrowDown className={`text-${color} opacity-0.5`} />
            </div>
            {isOpen &&
                <div
                    className={`lg:absolute origin-top-right focus:outline-none border w-full rounded-lg border-${color} bg-gableGreen`}
                    role="menu"
                >
                    {options.map((item, i) => (
                        <div
                            key={i}
                            onClick={() => handleSelect(item)}
                            className={`text-${color} cursor-pointer text-xs sm:text-sm xl:text-base block px-4 py-1 xl:py-2 hover:bg-lightGrey rounded-lg`}
                        >
                            {item}
                        </div>
                    ))}
                </div>
            }
        </div>
    );
}

export default Select